// TYPES
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuthConfigAffiliationType } from 'hooks/useFeatureFlag';
import { castArray, isEqual } from 'lodash';
import MigrateLegacyAffiliationsButton from 'pages/userManagement/authConfigs/affiliations/MigrateLegacyAffiliationsButton';
import {
  AffiliationFieldKey,
  BulkCreateResult,
} from 'pages/userManagement/authConfigs/affiliations/useBulkCreateAffiliations';
import { useCountAffiliationsQuery } from 'pages/userManagement/authConfigs/api/authConfig.api';
import { authconfigByIdSelector } from 'slices/authConfig.slice';
import { organizationSelector } from 'slices/organization.slice';
import { useAppSelector } from 'slices/store';
import { parseCustomerSignature } from 'utils/customerSignature';
import {
  EApplySSOPermissions,
  EAuthSaml2SigantureAlgorithm,
  type ISAML2Config,
  SAMLIdentifierFormat,
  type TIdPMetaData,
} from '@timeedit/types';
import {
  Button,
  ConfirmationPopup,
  Icon,
  Segmented,
  TEInput,
  Tooltip,
  Typography,
} from '@timeedit/ui-components';
import {
  Checkbox,
  Collapse,
  Flex,
  Form,
  Input,
  Select,
} from '@timeedit/ui-components/antd';
import type { CheckboxChangeEvent } from '@timeedit/ui-components/antd/checkbox/Checkbox';
// i18N
import intl, { getInlineString } from '../../../i18n/intl';
import api from '../../../services/api.service';
import UserSelectComponent from '../../SelectComponents/UserSelectComponent';
// COMPONENTS
import { TEToolTip } from '../../TEToolTip';
import AttributesParsingTable from '../AttributesParsingTable';
import CertificateList from '../CertificateList';
import PartialAttributesMatchingTable from '../PartialAttributesMatchingTable';
import './SAML2Config.scss';

const language: any = intl.messages;

type Props = {
  authConfigId: string;
  onChange: (prop: string, value: any) => void;
  config: ISAML2Config;
  originalConfig?: ISAML2Config;
  hasEditPermission: boolean;
};

const SAML2Config = ({
  authConfigId,
  onChange,
  config,
  originalConfig,
  hasEditPermission,
}: Props) => {
  const [isLoadingFromMetaData, setIsLoadingFromMetaData] = useState(false);
  const affiliationConfigType = useAuthConfigAffiliationType();

  // --- Migration error state ---
  // fieldIndices: rows where FE validation caught specific invalid inputs.
  // rowIndices:   rows where the API rejected the payload but we can't tell
  //               which field is wrong — falls back to a whole-row border.
  const [failedFieldIndices, setFailedFieldIndices] = useState<
    Map<number, Set<AffiliationFieldKey>>
  >(new Map());
  const [failedRowIndices, setFailedRowIndices] = useState<Set<number>>(
    new Set()
  );

  // Clear error borders whenever a save succeeds. saveAuthConfigSuccess calls
  // upsertEntity which creates a new object reference in the Redux map, so
  // this effect only fires on actual saves (not on failures).
  const savedConfig = useAppSelector((state) =>
    authconfigByIdSelector(state, authConfigId)
  );
  useEffect(() => {
    setFailedFieldIndices(new Map());
    setFailedRowIndices(new Set());
  }, [savedConfig]);

  const handleMigrationComplete = (results: BulkCreateResult[]) => {
    const fields = new Map<number, Set<AffiliationFieldKey>>();
    const rows = new Set<number>();
    for (const r of results) {
      if (!r.error) continue;
      if (r.fieldErrors?.size) {
        fields.set(r.rowIdx, r.fieldErrors);
      } else {
        rows.add(r.rowIdx);
      }
    }
    setFailedFieldIndices(fields);
    setFailedRowIndices(rows);
  };
  // --- end migration error state ---

  const isDisabled = (isDisabled: boolean) => {
    return !hasEditPermission || isDisabled;
  };

  // SLO opt-in requires an IdP logout URL. sloEnabled without one is a
  // misconfiguration that is treated as "not enabled" — surface it, never
  // half-enable. Mirrors isSloEnabled() in auth-be.
  const sloMisconfigured = Boolean(config.sloEnabled) && !config.idpLogoutUrl;

  const legacyRowsDirty = useMemo(
    () =>
      !isEqual(
        config.partialAttributesMatching ?? [],
        originalConfig?.partialAttributesMatching ?? []
      ),
    [
      config.partialAttributesMatching,
      originalConfig?.partialAttributesMatching,
    ]
  );

  // EVENT HANDLER
  const onPerformChange = (prop: string, value: any) => {
    console.log(prop, value);
    console.log({ ...config, [prop]: value });
    onChange('config', { ...config, [prop]: value });
  };

  const onChangeUserObjectField = (index: number, value: any) => {
    const currState = config.userObjectField;
    if (!currState) {
      onPerformChange('userObjectField', [
        index === 0 ? value : '',
        index === 1 ? value : '',
      ]);
    } else {
      onPerformChange('userObjectField', [
        index === 0 ? value : currState[0],
        index === 1 ? value : currState[1],
      ]);
    }
  };

  const addCertificate = (certToAdd: string) => {
    const certificatesWithAddedCert = [
      ...(castArray(config.idpPublicKey) || []),
      certToAdd,
    ];
    // Make sure no undefined or empty certs are present
    const filteredCerts = certificatesWithAddedCert.filter(
      (existingCert) => existingCert
    );
    onPerformChange('idpPublicKey', filteredCerts);
  };

  const removeCertificate = (certToRemove: string) => {
    const certificatesWithRemovedCert = castArray(config.idpPublicKey).filter(
      (existingCert) => existingCert !== certToRemove
    );
    // Make sure no undefined or empty certs are present
    const filteredCerts = certificatesWithRemovedCert.filter(
      (existingCert) => !!existingCert
    );

    onPerformChange('idpPublicKey', filteredCerts);
  };

  const importFromMetadata = () => {
    setIsLoadingFromMetaData(true);
    const { idpMetaDataUrl, idpEntityId } = config;
    api
      .post({
        endpoint: `/auth-configs/saml/parse-idp-metadata`,
        data: { idpMetaDataUrl, idpEntityId },
      })
      .then((idpMetaData: TIdPMetaData) => {
        setIsLoadingFromMetaData(false);
        if (!idpMetaData) return;

        // Metadata may omit a SingleLogoutService; don't clobber a manually
        // entered logout URL with an empty value in that case.
        const { idpLogoutUrl, ...rest } = idpMetaData;
        const newConfig: ISAML2Config = {
          ...config,
          ...rest,
          idpLogoutUrl: idpLogoutUrl || config.idpLogoutUrl,
        };
        onChange('config', newConfig);
      })
      .catch((error: any) => {
        setIsLoadingFromMetaData(false);
        console.error(error);
      });
  };

  return (
    <Flex className="saml2Config-container" vertical gap="small">
      <TEInput.LabelWrapper
        label={language.timeedit_entity_id}
        tooltip={language.help_timeedit_entity_id}
      >
        <Input
          size="small"
          value={config.spEntityId}
          onChange={(event) =>
            onPerformChange('spEntityId', event.target.value)
          }
        />
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={language.idp_entity_id}
        tooltip={language.help_idp_entity_id}
      >
        <Input
          size="small"
          value={config.idpEntityId || undefined}
          onChange={(event) =>
            onPerformChange('idpEntityId', event.target.value)
          }
        />
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={language.idp_metadata_url}
        tooltip={language.help_idp_metadata_url}
      >
        <Input
          size="small"
          value={config.idpMetaDataUrl || undefined}
          onChange={(event) =>
            onPerformChange('idpMetaDataUrl', event.target.value)
          }
        />
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={'NameID Format'}
        tooltip={
          'Define which NameID format the saml request should ask for. Some idps will want a specific format, to match the format they send back in the response'
        }
      >
        <Select
          size="small"
          options={Object.values(SAMLIdentifierFormat).map((format) => ({
            value: format,
          }))}
          value={config.identifierFormat || SAMLIdentifierFormat.Email}
          onChange={(event) => onPerformChange('identifierFormat', event)}
        />
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={language.signature_algorithm}
        tooltip={language.help_signature_algorithm}
      >
        <Select
          size="small"
          showSearch
          // passport-saml defaults to sha1 if not set, so we make sure to reflect that behavior here
          value={config.signatureAlgorithm || EAuthSaml2SigantureAlgorithm.SHA1}
          onChange={(event) => onPerformChange('signatureAlgorithm', event)}
        >
          {Object.entries(EAuthSaml2SigantureAlgorithm).map(([key, value]) => (
            <Select.Option value={value} key={key}>
              {key}
            </Select.Option>
          ))}
        </Select>
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={language.label_prefer_http_post}
        tooltip={language.help_prefer_http_post}
      >
        <Checkbox
          disabled={isDisabled(isLoadingFromMetaData)}
          checked={config.preferHttpPost}
          onChange={(event: any) =>
            onPerformChange('preferHttpPost', event.target.checked)
          }
        >
          {language.prefer_http_post}
        </Checkbox>
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label="Sign Authentication Request"
        tooltip={
          'Define whether the SAML Request should be signed or not, allowing the IdP to verify the signature using the TimeEdit public certificate.'
        }
      >
        <Checkbox
          checked={config?.signAuthnRequest}
          onChange={(event: any) =>
            onPerformChange('signAuthnRequest', event.target.checked)
          }
        >
          Use signed SAML Request
        </Checkbox>
      </TEInput.LabelWrapper>
      <TEInput.LabelWrapper
        label={language.label_slo_enabled}
        tooltip={language.help_slo_enabled}
      >
        <Checkbox
          checked={config.sloEnabled}
          onChange={(event: CheckboxChangeEvent) =>
            onPerformChange('sloEnabled', event.target.checked)
          }
        >
          {language.slo_enabled}
        </Checkbox>
      </TEInput.LabelWrapper>
      <Form.Item className="saml2Config-autofill-button">
        <TEToolTip helpText={language.help_autofill_from_metadata}>
          <div style={{ display: 'inline-block' }}>
            <ConfirmationPopup
              key="import"
              icon={<Icon id="Warning" alt="Warning" />}
              message="You are about to overwrite the IdP Entrypoint Url and all Certificates, changes are not permanent until you save the auth config."
              onConfirm={importFromMetadata}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="default"
                size="small"
                disabled={isDisabled(
                  !config.idpMetaDataUrl || !config.idpEntityId
                )}
                loading={isLoadingFromMetaData}
              >
                {language.autofill_from_metadata}
              </Button>
            </ConfirmationPopup>
          </div>
        </TEToolTip>
      </Form.Item>
      <Form.Item
        label={language.idp_entry_point_url}
        tooltip={language.help_idp_entry_point_url}
      >
        <Input
          size="small"
          disabled={isDisabled(isLoadingFromMetaData)}
          value={config.idpEntryPointUrl || undefined}
          onChange={(event) =>
            onPerformChange('idpEntryPointUrl', event.target.value)
          }
        />
      </Form.Item>
      <Form.Item
        label={language.idp_logout_url}
        tooltip={language.help_idp_logout_url}
        validateStatus={sloMisconfigured ? 'error' : undefined}
        help={
          sloMisconfigured ? language.error_slo_missing_logout_url : undefined
        }
      >
        <Input
          size="small"
          disabled={isDisabled(isLoadingFromMetaData)}
          value={config.idpLogoutUrl || undefined}
          onChange={(event) =>
            onPerformChange('idpLogoutUrl', event.target.value)
          }
        />
      </Form.Item>
      <Form.Item
        label={language.idp_public_key}
        tooltip={language.help_idp_public_key}
      >
        <CertificateList
          encodedCertificates={castArray(config.idpPublicKey).filter(
            (cert) => !!cert
          )}
          disabled={isDisabled(isLoadingFromMetaData)}
          onAdd={addCertificate}
          onRemove={removeCertificate}
        />
      </Form.Item>
      <Collapse defaultActiveKey={['1']} ghost style={{ fontSize: 16 }}>
        <Collapse.Panel
          header={
            <Typography.Title level={5}>
              {language.attributes_parsing}
            </Typography.Title>
          }
          key="2"
        >
          <Flex vertical gap="large">
            <Flex vertical gap="small">
              <TEInput.LabelWrapper
                label={language.user_name_attribute}
                tooltip={language.help_user_name_attribute}
              >
                <Input
                  size="small"
                  value={config.userNameAttribute || undefined}
                  onChange={(event: any) =>
                    onPerformChange('userNameAttribute', event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
              <TEInput.LabelWrapper
                label={language.email_attribute}
                tooltip={language.help_email_attribute}
              >
                <Input
                  size="small"
                  value={config.emailAttribute || undefined}
                  onChange={(event: any) =>
                    onPerformChange('emailAttribute', event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
              <TEInput.LabelWrapper
                label={language.given_name_attribute}
                tooltip={language.help_given_name_attribute}
              >
                <Input
                  size="small"
                  value={config.givenNameAttribute || undefined}
                  onChange={(event: any) =>
                    onPerformChange('givenNameAttribute', event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
              <TEInput.LabelWrapper
                label={language.last_name_attribute}
                tooltip={language.help_last_name_attribute}
              >
                <Input
                  size="small"
                  value={config.lastNameAttribute || undefined}
                  onChange={(event: any) =>
                    onPerformChange('lastNameAttribute', event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
            </Flex>
            <Flex vertical gap="small">
              <TEInput.LabelWrapper
                label={language.user_object_attribute}
                tooltip={language.help_user_object_attribute}
              >
                <Input
                  size="small"
                  value={
                    config.userObjectField
                      ? config.userObjectField[0]
                      : undefined
                  }
                  onChange={(event) =>
                    onChangeUserObjectField(0, event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
              <TEInput.LabelWrapper
                label={language.user_object_extid}
                tooltip={language.help_user_object_extid}
              >
                <Input
                  size="small"
                  value={config.userObjectExtId || undefined}
                  onChange={(event) =>
                    onPerformChange('userObjectExtId', event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
              <TEInput.LabelWrapper
                label={language.user_object_field}
                tooltip={language.help_user_object_field}
              >
                <Input
                  size="small"
                  value={
                    config.userObjectField
                      ? config.userObjectField[1]
                      : undefined
                  }
                  onChange={(event) =>
                    onChangeUserObjectField(1, event.target.value)
                  }
                />
              </TEInput.LabelWrapper>
              <TEInput.LabelWrapper
                label={language.mandatory_user_object}
                tooltip={language.help_mandatory_user_object}
              >
                <Checkbox
                  checked={config.requireUserObject}
                  onChange={(event) =>
                    onPerformChange('requireUserObject', event.target.checked)
                  }
                />
              </TEInput.LabelWrapper>
              <div className="saml2Config-attribute-parse-table-container">
                <TEToolTip helpText={language.help_mandatory_attributes}>
                  <Typography.Text strong>
                    {language.mandatory_attributes}
                  </Typography.Text>
                </TEToolTip>
                <AttributesParsingTable
                  attributePairs={config.mandatoryAttributes}
                  onChange={(attributePairs) =>
                    onPerformChange('mandatoryAttributes', attributePairs)
                  }
                />
              </div>
              <div className="saml2Config-attribute-parse-table-container">
                <TEToolTip helpText={language.help_forbidden_attributes}>
                  <Typography.Text strong>
                    {language.forbidden_attributes}
                  </Typography.Text>
                </TEToolTip>
                <AttributesParsingTable
                  attributePairs={config.forbiddenAttributes}
                  onChange={(attributePairs) =>
                    onPerformChange('forbiddenAttributes', attributePairs)
                  }
                />
              </div>
            </Flex>
          </Flex>
        </Collapse.Panel>
      </Collapse>
      <Collapse defaultActiveKey={['1']} ghost style={{ fontSize: 16 }}>
        <Collapse.Panel
          header={
            <Typography.Title level={5}>
              {language.permissions}
            </Typography.Title>
          }
          key="2"
        >
          <Flex gap="small" vertical>
            <TEInput.LabelWrapper
              label={language.set_permissions}
              tooltip={language.help_set_permissions}
            >
              <Select
                size="small"
                showSearch
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                value={language[config.applySSOPermissions]}
                onChange={(event) =>
                  onPerformChange('applySSOPermissions', event)
                }
              >
                {Object.entries(EApplySSOPermissions).map(([key, value]) => (
                  <Select.Option value={value} key={key}>
                    {language[value]}
                  </Select.Option>
                ))}
              </Select>
            </TEInput.LabelWrapper>
            <UserSelectComponent
              isMultiSelect={false}
              value={config.templateUser}
              onChange={onPerformChange}
              label={'Create new users based on (template user)'}
              tooltip={
                'When a user signs in for the first time and no matching user exists, a new user is created based on the selected template user (inheriting its settings and permissions).'
              }
              propName={'templateUser'}
            />
            <TEInput.LabelWrapper
              label={language.require_full_attribute_matches}
              tooltip={language.help_require_full_attribute_matches}
            >
              <Checkbox
                checked={config.requireFullAttributeMatches}
                onChange={(event: any) =>
                  onPerformChange(
                    'requireFullAttributeMatches',
                    event.target.checked
                  )
                }
              />
            </TEInput.LabelWrapper>
            <TEInput.LabelWrapper
              label={language.affiliation_matching}
              tooltip={language.help_allow_multiple_affiliation_matches}
            >
              <Segmented<boolean>
                className="saml2Config-affiliation-matching-segmented"
                value={config.allowMultipleAffiliationMatches ?? false}
                options={[
                  {
                    value: false,
                    label: language.allow_single_affiliation_match,
                  },
                  {
                    value: true,
                    label: language.allow_multiple_affiliation_matches,
                  },
                ]}
                onChange={(value) =>
                  onPerformChange('allowMultipleAffiliationMatches', value)
                }
              />
            </TEInput.LabelWrapper>
            <Flex gap="small">
              <Typography.Title level={5}>
                {language.affiliations}
              </Typography.Title>
              <TEToolTip helpText={language.help_affiliations}>
                <Icon id="QuestionCircleOutlined" alt="Help" />
              </TEToolTip>
            </Flex>
            {['both', 'embedded'].includes(affiliationConfigType) && (
              <PartialAttributesMatchingTable
                partialAttributes={config.partialAttributesMatching}
                onChange={(partialAttributes) =>
                  onPerformChange(
                    'partialAttributesMatching',
                    partialAttributes
                  )
                }
                failedFieldIndices={failedFieldIndices}
                failedRowIndices={failedRowIndices}
              />
            )}
            {['both', 'separated'].includes(affiliationConfigType) && (
              <AffiliationsSummary
                authConfigId={authConfigId}
                legacyRows={config.partialAttributesMatching ?? []}
                legacyRowsDirty={legacyRowsDirty}
                showMigrateLegacy={affiliationConfigType === 'both'}
                onMigrationComplete={handleMigrationComplete}
              />
            )}
          </Flex>
        </Collapse.Panel>
      </Collapse>
    </Flex>
  );
};

const AffiliationsSummary = ({
  authConfigId,
  legacyRows,
  legacyRowsDirty,
  showMigrateLegacy,
  onMigrationComplete,
}: {
  authConfigId: string;
  legacyRows: ISAML2Config['partialAttributesMatching'];
  legacyRowsDirty: boolean;
  showMigrateLegacy: boolean;
  onMigrationComplete?: (results: BulkCreateResult[]) => void;
}) => {
  const organization = useAppSelector(organizationSelector);
  const customerSignature = organization?.customerSignature;
  const parsedCustomerSignature =
    parseCustomerSignature(customerSignature) ?? '';

  const { data, isSuccess: isCountLoaded } = useCountAffiliationsQuery(
    {
      authConfigId,
    },
    {
      skip: !authConfigId || authConfigId === 'new',
    }
  );

  return (
    <Flex
      gap="middle"
      align="center"
      justify="flex-start"
      className="affiliation-container"
    >
      <Typography.Text>
        {getInlineString('affiliations_count', data?.total ?? 0)}
      </Typography.Text>
      <Link
        to={`/${parsedCustomerSignature}/user-management/auth-configs/${authConfigId}/affiliations`}
        style={{ display: 'inline-flex' }}
      >
        <Tooltip
          title={
            authConfigId === 'new'
              ? language.manage_affiliations_tooltip
              : undefined
          }
        >
          <Button
            size="small"
            disabled={!authConfigId || authConfigId === 'new'}
          >
            {language.manage_affiliations}
          </Button>
        </Tooltip>
      </Link>
      {showMigrateLegacy && authConfigId !== 'new' && isCountLoaded && (
        <MigrateLegacyAffiliationsButton
          authConfigId={authConfigId}
          legacyRows={legacyRows}
          legacyRowsDirty={legacyRowsDirty}
          existingNewAffiliationsCount={data.total}
          onMigrationComplete={onMigrationComplete}
        />
      )}
    </Flex>
  );
};

export default SAML2Config;
